import { QUANTITY_SCALE, parseHumanAmount, type Decimal } from '@aeris/money'

/**
 * What the operator typed into the till's one field.
 *
 * Every Brazilian till for thirty years has accepted `3*` before a code, and an
 * operator who has used one expects it. It is also the only way to sell three
 * of something with a scanner without scanning it three times, so it is not a
 * convenience — it is the difference between the till being usable and not.
 *
 * Pure and exported so it can be tested without a browser: the parsing is small
 * and it is exactly the sort of small thing that goes wrong quietly. `3x10` is
 * three of code `10`, not thirty; `7891234567890` is a barcode and not an
 * arithmetic expression; `PROD-3` keeps its hyphen.
 */

export const ONE_UNIT: Decimal = {
  units: 10n ** BigInt(QUANTITY_SCALE),
  scale: QUANTITY_SCALE,
}

/** `3*`, `3 x `, `2,5*` — a quantity, a separator, then the code. */
const MULTIPLIER = /^(\d+(?:[.,]\d+)?)\s*[*x]\s*(.+)$/i

export type ScanInput = {
  /** The code, barcode or name fragment to look up. Never empty. */
  readonly code: string
  /** How many. One unless the operator said otherwise. */
  readonly quantity: Decimal
}

/**
 * Splits a typed term into a quantity and a code.
 *
 * Returns null for an empty term rather than a zero-quantity lookup: pressing
 * Enter on an empty field is an operator collecting their thoughts, not a
 * request.
 */
export const parseScanInput = (raw: string): ScanInput | null => {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null

  const parts = MULTIPLIER.exec(trimmed)
  if (parts === null) return { code: trimmed, quantity: ONE_UNIT }

  const [, typedQuantity = '1', code = ''] = parts
  const quantity = parseHumanAmount(typedQuantity, QUANTITY_SCALE)

  // A quantity that will not parse, or is zero, falls back to one rather than
  // refusing: the operator clearly meant to buy the thing they named.
  return {
    code: code.trim(),
    quantity: quantity === null || quantity.units <= 0n ? ONE_UNIT : quantity,
  }
}
